import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection, type EditorState, type Transaction } from "@tiptap/pm/state";
import { Decoration, DecorationSet, type EditorView } from "@tiptap/pm/view";
import { defaultCaseBrief, defaultCaseBriefJapanese, retiredTemplateLines } from "../const/defaultCaseBrief";

type TPlaceholderMatchers = { whole: Set<string>; labelled: Map<string, string[]> };

const LABELLED = [/^\*\*(.+?):\*\*\s*(.+)$/, /^\*\*(.+?)\*\*:\s*(.+)$/];
const stripMarkdown = (text: string) => text.replace(/\*\*|__/g, "").replace(/\s+/g, " ").trim();

// The template's prompt lines, so they can be told apart from content that replaced them.
export function buildPlaceholderMatchers(templates: string[]): TPlaceholderMatchers {
  const whole = new Set<string>();
  const labelled = new Map<string, string[]>();
  for (const template of templates) {
    for (const raw of template.split("\n")) {
      const line = raw.trim().replace(/^[-*]\s+/, "");
      if (!line || line.startsWith("#")) continue;
      const match = LABELLED.map((pattern) => line.match(pattern)).find(Boolean);
      if (!match) {
        whole.add(stripMarkdown(line));
        continue;
      }
      const label = stripMarkdown(match[1]);
      labelled.set(label, [...(labelled.get(label) ?? []), stripMarkdown(match[2])]);
    }
  }
  return { whole, labelled };
}

// The part of a line that is still template text: the whole line, or the prompt after a label
// ("Decision Question: What decision are you trying to make?"). Offsets are within `text`.
export function placeholderRange(text: string, matchers: TPlaceholderMatchers): [number, number] | null {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return null;
  if (matchers.whole.has(normalized)) return [text.indexOf(normalized[0]), text.length];
  for (const [label, prompts] of matchers.labelled) {
    if (!normalized.startsWith(`${label}:`)) continue;
    const prompt = prompts.find((candidate) => normalized.slice(label.length + 1).trim() === candidate);
    if (!prompt) continue;
    const start = text.lastIndexOf(prompt);
    if (start >= 0) return [start, start + prompt.length];
  }
  return null;
}

const matchers = buildPlaceholderMatchers([defaultCaseBrief, defaultCaseBriefJapanese, ...retiredTemplateLines]);

function promptRangeAt(state: EditorState, from: number, to: number): [number, number] | null {
  const $from = state.doc.resolve(from);
  const block = $from.parent;
  if (!block.isTextblock || block.type.name === "heading" || to > $from.end()) return null;
  const range = placeholderRange(block.textContent, matchers);
  if (!range) return null;
  const start = $from.start() + range[0];
  const end = $from.start() + range[1];
  const afterLabel = range[0] > 0 && block.textContent[range[0] - 1] === " " ? start - 1 : start;
  return from < afterLabel || to > end ? null : [start, end];
}

// A prompt the user cleared, by typing, pasting or deleting into it: `pos` is the start of its line,
// `labelled` whether the line keeps a "Label:" before it.
type TClearedPrompt = { pos: number; prompt: string; labelled: boolean };
type TClearedMeta = { add?: TClearedPrompt; remove?: number[] };

const pluginKey = new PluginKey<TClearedPrompt[]>("templatePlaceholders");

// Records the prompt at `range` as cleared by `tr`, so it comes back if the user leaves its line empty.
function clearingPrompt(tr: Transaction, state: EditorState, [start, end]: [number, number]) {
  const lineStart = state.doc.resolve(start).start();
  const add: TClearedPrompt = { pos: lineStart, prompt: state.doc.textBetween(start, end), labelled: start > lineStart };
  return tr.setMeta(pluginKey, { add } satisfies TClearedMeta);
}

// The line holds nothing of the user's: only its label, or nothing at all.
function isLeftEmpty(state: EditorState, cleared: TClearedPrompt) {
  const line = state.doc.resolve(cleared.pos).parent;
  if (!line.isTextblock) return false;
  return cleared.labelled ? /^[^:]+:\s*$/.test(line.textContent) : !line.textContent.trim();
}

// Once the cursor is off a cleared line, or the editor loses focus, an empty line gets its prompt back
// and a line the user wrote in is forgotten. Dispatched as an edit of its own, so the editor reports it
// and the brief saves it: an insert appended to a click or a blur would show but never be saved.
function restoreLeftPrompts(view: EditorView, blurred: boolean) {
  const { state } = view;
  const cleared = pluginKey.getState(state) ?? [];
  const cursorLine = state.selection.$head.start();
  const left = cleared.filter((c) => blurred || state.doc.resolve(c.pos).start() !== cursorLine);
  if (left.length === 0) return;
  const tr = state.tr;
  // From the end of the brief backwards, so each insert leaves the earlier positions as they are.
  for (const c of [...left].sort((a, b) => b.pos - a.pos)) {
    if (!isLeftEmpty(state, c)) continue;
    const $line = state.doc.resolve(c.pos);
    const space = c.labelled && !/\s$/.test($line.parent.textContent) ? " " : "";
    tr.insert($line.end(), state.schema.text(space + c.prompt));
  }
  view.dispatch(tr.setMeta(pluginKey, { remove: left.map((c) => c.pos) } satisfies TClearedMeta));
}

// Shows the brief's untouched template prompts in grey; the text itself is unchanged. Off until
// `storage.enabled` is set, which the editor does only for organizations with document reading.
// `labelClassName`, when set, also styles the label of an untouched "Label: prompt" line.
// A prompt behaves as one piece: typing, pasting or deleting into it clears all of it, and a line the
// user then leaves with nothing of theirs gets its prompt back.
export const TemplatePlaceholders = Extension.create<
  object,
  { enabled: boolean; className: string; labelClassName: string | null }
>({
  name: "templatePlaceholders",
  addStorage() {
    return { enabled: false, className: "text-innoveraZinc-400", labelClassName: null };
  },
  onSelectionUpdate() {
    if (this.storage.enabled) restoreLeftPrompts(this.editor.view, false);
  },
  onBlur() {
    if (this.storage.enabled) restoreLeftPrompts(this.editor.view, true);
  },
  addProseMirrorPlugins() {
    const storage = this.storage;
    return [
      new Plugin<TClearedPrompt[]>({
        key: pluginKey,
        state: {
          init: () => [],
          apply(tr, cleared) {
            const meta = tr.getMeta(pluginKey) as TClearedMeta | undefined;
            const kept = meta?.remove ? cleared.filter((c) => !meta.remove?.includes(c.pos)) : cleared;
            const next = meta?.add ? [...kept, meta.add] : kept;
            if (!tr.docChanged) return next;
            // A line that was removed or replaced, as when the brief is reloaded, has nothing to restore.
            return next.flatMap((c) => {
              const mapped = tr.mapping.mapResult(c.pos, -1);
              return mapped.deleted ? [] : [{ ...c, pos: mapped.pos }];
            });
          }
        },
        props: {
          handleTextInput(view, from, to, text) {
            const prompt = storage.enabled && !view.composing ? promptRangeAt(view.state, from, to) : null;
            if (!prompt) return false;
            view.dispatch(clearingPrompt(view.state.tr.insertText(text, prompt[0], prompt[1]), view.state, prompt));
            return true;
          },
          // Deleting any part of a prompt deletes all of it, so no half-prompt is left looking like an answer.
          handleKeyDown(view, event) {
            if (!storage.enabled || view.composing || (event.key !== "Backspace" && event.key !== "Delete")) return false;
            const { from, to, empty } = view.state.selection;
            const [deleteFrom, deleteTo] = !empty ? [from, to] : event.key === "Backspace" ? [from - 1, from] : [from, from + 1];
            const prompt = deleteFrom >= 0 ? promptRangeAt(view.state, deleteFrom, deleteTo) : null;
            if (!prompt) return false;
            const tr = view.state.tr.delete(prompt[0], prompt[1]);
            view.dispatch(clearingPrompt(tr.setSelection(TextSelection.create(tr.doc, prompt[0])), view.state, prompt));
            return true;
          },
          handleDOMEvents: {
            // Replacing text under an active IME composition breaks it, so the prompt goes first.
            compositionstart(view) {
              const { from, to } = view.state.selection;
              const prompt = storage.enabled ? promptRangeAt(view.state, from, to) : null;
              if (prompt) {
                const tr = view.state.tr.delete(prompt[0], prompt[1]);
                view.dispatch(clearingPrompt(tr.setSelection(TextSelection.create(tr.doc, prompt[0])), view.state, prompt));
              }
              return false;
            }
          },
          handlePaste(view, _event, slice) {
            const { from, to } = view.state.selection;
            const prompt = storage.enabled ? promptRangeAt(view.state, from, to) : null;
            if (!prompt) return false;
            view.dispatch(clearingPrompt(view.state.tr.replaceRange(prompt[0], prompt[1], slice).scrollIntoView(), view.state, prompt));
            return true;
          },
          decorations(state) {
            if (!storage.enabled) return DecorationSet.empty;
            const decorations: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (!node.isTextblock || node.type.name === "heading") return true;
              const range = placeholderRange(node.textContent, matchers);
              if (range) {
                decorations.push(Decoration.inline(pos + 1 + range[0], pos + 1 + range[1], { class: storage.className }));
                if (storage.labelClassName && range[0] > 0) {
                  decorations.push(Decoration.inline(pos + 1, pos + 1 + range[0], { class: storage.labelClassName }));
                }
              }
              return false;
            });
            return DecorationSet.create(state.doc, decorations);
          }
        }
      })
    ];
  }
});
